import React, { useRef, useState } from 'react';

import PageLayout from '@/components/layout/PageLayout';
import { PermissionGuard } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { DateRangeFilter } from '@/components/ui/filters/DateRangeFilter';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { IconScout } from '@/components/ui/images/IconScout';
import Table from '@/components/ui/tables/Table';
import { Tooltip } from '@/components/ui/tooltips/Tooltip';
import { useTranslation } from '@/i18n';
import { useShiftAssignmentList } from '@/hooks/shiftAssignment';


/**
 * Histórico de asignaciones de turnos (ASIS-5656).
 *
 * Los controles de "Nueva asignación" (ASIS-5661), "Descargar" (ASIS-5657), la edición
 * masiva (ASIS-5660) y el detalle de fila (ASIS-5658) se renderizan visibles y
 * deshabilitados con un tooltip explicativo: así el layout no cambia de forma cuando esas
 * historias se implementen, y el usuario entiende por qué la acción no está disponible.
 */
export const ShiftAssignmentList: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    bulkActions,
    onSearch,
    onEnterSearch,
    onSortChange,
    isLoading,
    filters,
    onFilterChange,
    dateRange,
    onDateRangeChange,
    shiftOptions,
    statusOptions,
    emptyMessage,
    comingSoonTooltip,
  } = useShiftAssignmentList();

  // El wrapper con handlers de mouse es necesario para que el Tooltip aparezca sobre un
  // botón deshabilitado (un botón disabled no emite eventos de puntero propios).
  const createButtonRef = useRef<HTMLDivElement>(null);
  const [showCreateTooltip, setShowCreateTooltip] = useState(false);

  // Spinner de página completa solo en la carga inicial: en refetch por filtro/orden el propio
  // Table ya muestra su loading interno, así que no hay que bloquear toda la pantalla de nuevo.
  const isInitialLoading = isLoading && rows.length === 0;

  return (
    <PageLayout isLoading={isInitialLoading}>
      <div data-testid="shift-assignments-page">
        <div className="mb-4">
          <PermissionGuard action="create">
            <div
              ref={createButtonRef}
              className="inline-block"
              onMouseEnter={() => setShowCreateTooltip(true)}
              onMouseLeave={() => setShowCreateTooltip(false)}
            >
              <PrimaryButton
                iconLeft={<IconScout name="plus" />}
                disabled
                data-testid="shift-assignment-create-button"
              >
                {translate('schedule:shiftAssignment.actions.create')}
              </PrimaryButton>
            </div>
          </PermissionGuard>
          {showCreateTooltip && (
            <Tooltip body={comingSoonTooltip} anchorRef={createButtonRef} placement="right" />
          )}
        </div>

        <div className="flex flex-wrap gap-4 justify-between items-start mb-4">
          <div className="flex flex-wrap gap-4 items-start">
            {/* DateRangeFilter no expone data-testid ni reenvía props extra:
                el testid va en el wrapper. */}
            <div data-testid="shift-assignment-date-range">
              <DateRangeFilter value={dateRange} onChange={onDateRangeChange} />
            </div>
            <FilterDropdown
              filters={[
                {
                  key: 'shiftId',
                  label: translate('schedule:shiftAssignment.filters.shift'),
                  options: shiftOptions,
                  placeholder: translate('schedule:shiftAssignment.filters.all'),
                },
                {
                  key: 'status',
                  label: translate('schedule:shiftAssignment.filters.status'),
                  options: statusOptions,
                  placeholder: translate('schedule:shiftAssignment.filters.all'),
                },
              ]}
              values={filters}
              onChangeFilter={onFilterChange}
            />
          </div>
          <PermissionGuard action="view" fallback="hidden">
            {/* La descarga llega en ASIS-5657: el único formato se muestra deshabilitado
                con el sufijo "(Próximamente)" que ya provee ExportDropdown. */}
            <ExportDropdown
              onExport={() => undefined}
              placement="right"
              availableFormats={['EXCEL']}
              disabledFormats={['EXCEL']}
            />
          </PermissionGuard>
        </div>

        <Table
          columns={columns}
          data={rows}
          totalRows={total}
          isLoading={isLoading}
          searchable
          onSearch={onSearch}
          onEnterSearch={onEnterSearch}
          serverSideSort
          onSortChange={onSortChange}
          actions={actions}
          selectable
          bulkActions={bulkActions}
          emptyMessage={emptyMessage}
          pagination={{
            page,
            pageSize,
            total,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          data-testid="shift-assignments-table"
        />
      </div>
    </PageLayout>
  );
};

export default ShiftAssignmentList;
